import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { breakfastEventsPrefix, pendingInvitesPrefix } from "../queryKeys";

/**
 * Add attendees to an existing event by email.
 *
 * Replaces the legacy `rpc('add_breakfast_attendees', …)` call with a
 * direct upsert into `breakfast_attendees`, which is now permitted by
 * RLS (`ba_insert WITH CHECK (true)` — host scoped at the UI layer).
 *
 * The payload shape is exactly the directive's canonical form:
 *   [{ event_id, user_id, status: 'pending' }, …]
 *
 * Email resolution step is preserved because the existing drawer UI
 * takes a guest *email* in the invite form (the modal's circle-member
 * picker already has the user_id directly and goes through
 * `useUpdateEventFull`). Every failure is rethrown as an `Error` so
 * React Query routes it into `onError`; no "fake success".
 */
export function useAddAttendees() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      eventId: string;
      inviteeEmails: string[];
    }) => {
      // 1. Resolve the given emails to profile ids.
      const { data: users, error: userErr } = await supabase
        .from("profiles")
        .select("id, email")
        .in("email", params.inviteeEmails);
      if (userErr) throw new Error(userErr.message);

      const foundEmails = new Set(
        (users ?? []).map((u) => u.email.toLowerCase()),
      );
      const missing = params.inviteeEmails.find(
        (e) => !foundEmails.has(e.toLowerCase()),
      );
      if (missing) throw new Error(`User not found: ${missing}`);

      // 2. Build the canonical insert payload — one row per selected user,
      //    all starting as `pending` RSVPs.
      const rows = (users ?? []).map((u) => ({
        event_id: params.eventId,
        user_id: u.id,
        status: "pending" as const,
      }));

      if (rows.length === 0) {
        throw new Error("No valid invitees to add.");
      }

      // 3. Upsert so re-adding an already-invited user is a no-op instead
      //    of a unique-constraint violation. Ask for the inserted ids
      //    back — same "fake success" guard pattern as the other writes.
      const { data: inserted, error: insertErr } = await supabase
        .from("breakfast_attendees")
        .upsert(rows, {
          onConflict: "event_id,user_id",
          ignoreDuplicates: false,
        })
        .select("id");
      if (insertErr) throw new Error(insertErr.message);

      return inserted ?? [];
    },
    onSuccess: () => {
      // NOTE: the registered query key is camelCase
      // `["breakfastEvents", userId, startDate, endDate]`. We invalidate by
      // prefix so every dated cache slice refetches and the new attendees
      // appear in the drawer immediately.
      queryClient.invalidateQueries({ queryKey: breakfastEventsPrefix });
      // Also poke the RSVP Center so any newly-invited user viewing the app
      // sees the invite surface immediately (Realtime will usually beat us
      // to it, but this guarantees a same-session refresh).
      queryClient.invalidateQueries({ queryKey: pendingInvitesPrefix });
    },
    onError: (err: Error) => {
      toast({
        title: "Could not add attendee",
        description: err?.message || "Please try again later.",
        variant: "destructive",
      });
    },
  });
}

/**
 * Remove a breakfast invite for the current user (self-dismiss).
 *
 * Thin wrapper around the `remove_breakfast_invite` RPC. Invalidates the
 * `breakfastEvents` cache on success so the UI picks up the removal.
 */
export function useRemoveEventInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventId: string) => {
      const { error } = await supabase.rpc("remove_breakfast_invite", {
        p_event_id: eventId,
      });
      if (error) throw error;
      return true;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: breakfastEventsPrefix }),
  });
}
